import React, { useState, useEffect, useCallback } from "react";

interface CountdownProps {
  targetDate: string | Date;
  onComplete?: () => void;
  showDays?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "compact" | "badge";
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

const Countdown: React.FC<CountdownProps> = ({
  targetDate,
  onComplete,
  showDays = true,
  className = "",
  size = "md",
  variant = "default",
}) => {
  const calculateTimeLeft = useCallback((): TimeLeft => {
    const target = new Date(targetDate).getTime();
    const now = new Date().getTime();
    const difference = target - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      ),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
      total: difference,
    };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.total <= 0) {
        clearInterval(timer);
        onComplete?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft, onComplete]);

  const padNumber = (num: number): string => num.toString().padStart(2, "0");

  if (timeLeft.total <= 0) {
    return null;
  }

  // Compact variant (inline text)
  if (variant === "compact") {
    const parts = [];
    if (showDays && timeLeft.days > 0) {
      parts.push(`${timeLeft.days}d`);
    }
    parts.push(`${padNumber(timeLeft.hours)}h`);
    parts.push(`${padNumber(timeLeft.minutes)}m`);
    parts.push(`${padNumber(timeLeft.seconds)}s`);

    return <span className={`font-mono ${className}`}>{parts.join(" ")}</span>;
  }

  // Badge variant
  if (variant === "badge") {
    const sizeClasses = {
      sm: "text-xs px-2 py-1",
      md: "text-sm px-3 py-1.5",
      lg: "text-base px-4 py-2",
    };

    return (
      <span
        className={`inline-flex items-center font-mono rounded bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white ${sizeClasses[size]} ${className}`}
      >
        {showDays && timeLeft.days > 0 && <span>{timeLeft.days}D </span>}{" "}
        <span>
          {padNumber(timeLeft.hours)}:{padNumber(timeLeft.minutes)}:
          {padNumber(timeLeft.seconds)}
        </span>
      </span>
    );
  }

  // Default variant (boxes)
  const sizeClasses = {
    sm: {
      container: "gap-1",
      box: "w-10 h-10",
      number: "text-sm",
      label: "text-[10px]",
    },
    md: {
      container: "gap-2",
      box: "w-14 h-14",
      number: "text-lg",
      label: "text-xs",
    },
    lg: {
      container: "gap-3",
      box: "w-20 h-20",
      number: "text-2xl",
      label: "text-sm",
    },
  };

  const styles = sizeClasses[size];

  const TimeBox = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center">
      <div
        className={`${styles.box} bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg`}
      >
        <span className={`${styles.number} font-bold text-white font-mono`}>
          {padNumber(value)}
        </span>
      </div>
      <span
        className={`${styles.label} text-gray-500 mt-1 uppercase tracking-wider`}
      >
        {label}
      </span>
    </div>
  );

  return (
    <div className={`flex ${styles.container} ${className}`}>
      {showDays && timeLeft.days > 0 && (
        <>
          <TimeBox value={timeLeft.days} label="Days" />
          <span
            className={`${styles.number} text-gray-400 self-center font-bold`}
          >
            :
          </span>
        </>
      )}
      <TimeBox value={timeLeft.hours} label="Hours" />
      <span className={`${styles.number} text-gray-400 self-center font-bold`}>
        :
      </span>
      <TimeBox value={timeLeft.minutes} label="Mins" />
      <span className={`${styles.number} text-gray-400 self-center font-bold`}>
        :
      </span>
      <TimeBox value={timeLeft.seconds} label="Secs" />
    </div>
  );
};

export default Countdown;
